import React, { useState, useEffect } from 'react';
import axios from '../utils/axiosConfig';
import { FaSpinner, FaCheck, FaTimes, FaFilm, FaTv, FaCalendarAlt, FaServer, FaDatabase, FaUserCog, FaBook, FaCopy, FaSave, FaKey } from 'react-icons/fa';

const HomePage = () => {
  const [syncStatus, setSyncStatus] = useState('unknown');
  const [dbStatus, setDbStatus] = useState({ status: 'unknown', responseTime: null });
  const [userInfo, setUserInfo] = useState({ subscriptionType: '', daysLeft: 0 });
  const [tutorials, setTutorials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dbCredentials, setDbCredentials] = useState({ host: '', user: '', password: '' });
  const [dbSaveMessage, setDbSaveMessage] = useState(null);
  const [tmdbKey, setTmdbKey] = useState('');
  const [isTmdbConfigured, setIsTmdbConfigured] = useState(true);
  const [tmdbSaveMessage, setTmdbSaveMessage] = useState(null);
  const [savingDb, setSavingDb] = useState(false);
  const [savingTmdb, setSavingTmdb] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const [syncResponse, dbResponse, userInfoResponse, tutorialsResponse, tmdbResponse] = await Promise.all([
          axios.get('/api/home/sync/status'),
          axios.get('/api/home/db/status'),
          axios.get('/api/home/user/info'),
          axios.get('/api/home/tutorials'),
          axios.get('/api/home/tmdb/status')
        ]);

        setSyncStatus(syncResponse.data.status);
        setDbStatus({
          status: dbResponse.data.status,
          responseTime: dbResponse.data.responseTime
        });
        setUserInfo(userInfoResponse.data);
        setTutorials(tutorialsResponse.data);
        setIsTmdbConfigured(tmdbResponse.data.isConfigured);
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Ocorreu um erro ao carregar os dados. Por favor, tente novamente mais tarde.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleDbCredentialsChange = (e) => {
    const { name, value } = e.target;
    setDbCredentials(prev => ({ ...prev, [name]: value }));
  };

  const handleSaveDbCredentials = async () => {
    try {
      setSavingDb(true);
      setDbSaveMessage(null);
      const response = await axios.post('/api/home/save-db-credentials', dbCredentials);
      if (response.data.success) {
        setDbStatus({ status: 'online', responseTime: response.data.responseTime });
        setDbCredentials({ host: '', user: '', password: '' });
        setDbSaveMessage({ type: 'success', text: 'Conexão XUI salva com sucesso.' });
      } else {
        setDbSaveMessage({ type: 'error', text: 'Não foi possível conectar ao banco de dados. Verifique suas credenciais.' });
      }
    } catch (error) {
      console.error('Error saving DB credentials:', error);
      setDbSaveMessage({ type: 'error', text: 'Ocorreu um erro ao salvar as credenciais. Por favor, tente novamente.' });
    } finally {
      setSavingDb(false);
    }
  };

  const handleSaveTmdbKey = async () => {
    try {
      setSavingTmdb(true);
      setTmdbSaveMessage(null);
      const response = await axios.post('/api/home/save-tmdb-key', { apiKey: tmdbKey });
      if (response.data.success) {
        setIsTmdbConfigured(true);
        setTmdbKey('');
        setTmdbSaveMessage({ type: 'success', text: 'Chave TMDB salva com sucesso.' });
      } else {
        setTmdbSaveMessage({ type: 'error', text: response.data.message || 'Não foi possível salvar a chave TMDB.' });
      }
    } catch (error) {
      console.error('Error saving TMDB key:', error);
      const message = error.response?.data?.message || 'Ocorreu um erro ao salvar a chave TMDB. Por favor, tente novamente.';
      setTmdbSaveMessage({ type: 'error', text: message });
    } finally {
      setSavingTmdb(false);
    }
  };

    const copyToClipboard = (text) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      // Use Clipboard API if available
      navigator.clipboard.writeText(text)
        .then(() => alert('Texto copiado para a área de transferência!'))
        .catch(err => console.error('Erro ao copiar texto: ', err));
    } else {
      // Fallback method using a temporary textarea element
      const textArea = document.createElement("textarea");
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        alert('Texto copiado para a área de transferência!');
      } catch (err) {
        console.error('Erro ao copiar texto: ', err);
      }
      document.body.removeChild(textArea);
    }
  };
  const renderTutorials = () => {
    return tutorials.map((tutorial, index) => (
      <div key={index} className="tutorial-card">
        <h3>{tutorial.title}</h3>
        {tutorial.content.split(/<pre class="copyable-text">([\s\S]*?)<\/pre>/g).map((part, i) => {
          if (i % 2 === 0) {
            return <div key={i} dangerouslySetInnerHTML={{ __html: part }} />;
          } else {
            return (
              <pre key={i} className="copyable-text">
                {part}
                <button className="copy-button" onClick={() => copyToClipboard(part)}>
                  <FaCopy />
                </button>
              </pre>
            );
          }
        })}
      </div>
    ));
  };
  if (loading) {
    return (
      <div className="home-page">
        <div className="card loading-card">
          <FaSpinner className="icon-spin" />
          <p>Carregando informações...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="home-page">
        <div className="card error-card">
          <FaTimes className="error-icon" />
          <h2>Erro</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="home-page">

      <div className="dashboard-grid">
        <div className="dashboard-card">
          <div className="card-icon">
            <FaServer />
          </div>
          <div className="card-content">
            <h3>Sincronizador</h3>
            <div className={`status ${syncStatus === 'online' ? 'status-online' : 'status-offline'}`}>
              {syncStatus === 'online' ? <FaCheck /> : <FaTimes />}
              <span>{syncStatus === 'online' ? 'Online' : 'Offline'}</span>
            </div>
          </div>
        </div>
        
        <div className="dashboard-card">
          <div className="card-icon">
            <FaDatabase />
          </div>
          <div className="card-content">
            <h3>Banco de Dados</h3>
            <div className={`status ${dbStatus.status === 'online' ? 'status-online' : 'status-offline'}`}>
              {dbStatus.status === 'online' ? <FaCheck /> : <FaTimes />}
              <span>{dbStatus.status === 'online' ? 'Online' : 'Offline'}</span>
            </div>
            {dbStatus.status === 'online' && (
              <p className="response-time">({dbStatus.responseTime}ms)</p>
            )}
          </div>
        </div>
        
        <div className="dashboard-card">
          <div className="card-icon">
            <FaUserCog />
          </div>
          <div className="card-content">
            <h3>Assinatura</h3>
            <p className="subscription-type">
              {userInfo.subscriptionType === 'Só Filmes' && <FaFilm />}
              {userInfo.subscriptionType === 'Só Séries' && <FaTv />}
              {userInfo.subscriptionType === 'Filmes e Séries' && <><FaFilm /> <FaTv /></>}
              <span>{userInfo.subscriptionType}</span>
            </p>
            <p className="subscription-days">
              <FaCalendarAlt />
              <span>{userInfo.daysLeft} dias restantes</span>
            </p>
          </div>
        </div>

        <div className="dashboard-card">
          <div className="card-icon">
            <FaKey />
          </div>
          <div className="card-content">
            <h3>TMDB</h3>
            <div className={`status ${isTmdbConfigured ? 'status-online' : 'status-offline'}`}>
              {isTmdbConfigured ? <FaCheck /> : <FaTimes />}
              <span>{isTmdbConfigured ? 'Configurado' : 'Não configurado'}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="db-config-section">
        <h2 className="section-title"><FaDatabase /> Conexão com o banco XUI</h2>
        <p className="section-hint">
          Informe o host, usuário e senha do banco de dados do seu servidor XUI. Pode ser
          editado a qualquer momento para apontar para outro servidor.
        </p>
        <div className="db-config-form">
          <input
            type="text"
            name="host"
            placeholder="Host do Banco de Dados"
            value={dbCredentials.host}
            onChange={handleDbCredentialsChange}
          />
          <input
            type="text"
            name="user"
            placeholder="Usuário do Banco de Dados"
            value={dbCredentials.user}
            onChange={handleDbCredentialsChange}
          />
          <input
            type="password"
            name="password"
            placeholder="Senha do Banco de Dados"
            value={dbCredentials.password}
            onChange={handleDbCredentialsChange}
          />
          <button className="btn btn-primary" onClick={handleSaveDbCredentials} disabled={savingDb}>
            {savingDb ? <FaSpinner className="icon-spin" /> : <FaSave />} Salvar e Testar Conexão
          </button>
        </div>
        {dbSaveMessage && (
          <p className={dbSaveMessage.type === 'success' ? 'form-message-success' : 'form-message-error'}>
            {dbSaveMessage.text}
          </p>
        )}
      </div>

      <div className="db-config-section">
        <h2 className="section-title"><FaKey /> Configuração TMDB</h2>
        <p className="section-hint">
          Informe sua chave da API do TMDB (themoviedb.org). Para usar mais de uma chave e
          distribuir as requisições, separe-as por vírgula. Pode ser trocada a qualquer momento.
        </p>
        <div className="db-config-form">
          <input
            type="text"
            name="tmdbKey"
            placeholder="Chave da API TMDB"
            value={tmdbKey}
            onChange={(e) => setTmdbKey(e.target.value)}
          />
          <button className="btn btn-primary" onClick={handleSaveTmdbKey} disabled={savingTmdb}>
            {savingTmdb ? <FaSpinner className="icon-spin" /> : <FaSave />} Salvar Chave TMDB
          </button>
        </div>
        {tmdbSaveMessage && (
          <p className={tmdbSaveMessage.type === 'success' ? 'form-message-success' : 'form-message-error'}>
            {tmdbSaveMessage.text}
          </p>
        )}
      </div>

      <div className="tutorials-section">
        <h2 className="section-title"><FaBook /> Tutoriais</h2>
    {renderTutorials()}
      </div>
    </div>
  );
};

export default HomePage;